import React, {
  useState,
  useEffect,
  useReducer,
  createContext,
  useContext,
  useRef, // Import useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import toast from 'react-hot-toast';

import {
  callFunction,
  createTask,
  fetchTasks,
  fetchTaskConversations,
  getUserInfo,
  deleteTask,
  streamChat,
  initializeStreamChat,
  createBotSteps,
  createTaskBot,
  fetchBots,
  listFiles,
  listFilesDataSource,
  getTaskDataSources,
  updateTaskDataSources,
  fetchBotsHistory,
  stopStreamChat,
  completeStreamChat,
} from '../services/api';
import { getWorkflows } from '../services/workflowApi';
import {
  functionsNeedingApproval,
  functionsNeedingApprovalForAction,
  functionsNeedingFileUpload,
  functionsNeedingFileUploadForAction,
} from '../utils/constants';
import TextFormat from '../components/TextFormat';
import useFileStatusPolling from '../hooks/useFileStatusPolling';
import {
  extractAndSetToolResponsesForReferencing,
  prepareFunctionArguments,
} from '../utils/helperFunctionsToolReferencing';

const buildAssistantMessageSignature = (message) => {
  if (!message || message.role !== 'assistant') {
    return null;
  }

  // Extract plain text content for a lenient comparison fallback.
  const contentArr = Array.isArray(message.content) ? message.content : [];
  const textContent = contentArr
    .map((block) => (block?.text || '').trim())
    .filter(Boolean)
    .join('|');

  const toolIds = (message.tool_calls || [])
    .map((tc) => tc?.id || '')
    .filter(Boolean)
    .sort()
    .join(',');

  // Use a simplified signature that is resilient to minor structural
  // differences between streaming-built and backend-built messages.
  return JSON.stringify({
    role: message.role,
    reasoning: (message.reasoning || '').trim() || '',
    text: textContent,
    toolIds,
    toolNames: (message.tool_calls || [])
      .map((tc) => tc?.function?.name || '')
      .filter(Boolean)
      .sort()
      .join(','),
  });
};

const isDuplicateFinalAssistantMessage = (messages, finalAssistantMessage) => {
  const incomingSignature = buildAssistantMessageSignature(
    finalAssistantMessage,
  );
  if (!incomingSignature) {
    return false;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message) {
      continue;
    }

    if (message.role === 'reason' || message.role === 'tool_calls_ui') {
      continue;
    }

    if (message.role !== 'assistant') {
      return false;
    }

    return buildAssistantMessageSignature(message) === incomingSignature;
  }

  return false;
};

const getReasoningToolCalls = (message) =>
  (message?.tool_calls || []).filter(
    (toolCall) => toolCall?.function?.name === 'record_thought_process',
  );

const getToolCallIdsSignature = (toolCalls) =>
  (toolCalls || [])
    .map((toolCall) => {
      if (toolCall?.id) return toolCall.id;
      return [
        toolCall?.function?.name || '',
        toolCall?.function?.arguments || '',
      ].join(':');
    })
    .filter(Boolean)
    .sort()
    .join(',');

const hasMatchingReasonRowInLatestSegment = (messages, reasonCalls) => {
  const reasonSignature = getToolCallIdsSignature(reasonCalls);
  if (!reasonSignature) return false;

  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      lastUserIndex = i;
      break;
    }
  }

  const segmentStart = lastUserIndex === -1 ? 0 : lastUserIndex + 1;
  return messages.slice(segmentStart).some((message) => {
    if (message?.role !== 'reason') return false;
    return getToolCallIdsSignature(message.tool_calls) === reasonSignature;
  });
};

const appendAssistantWithReasoning = (state, assistantMessage) => {
  if (isDuplicateFinalAssistantMessage(state, assistantMessage)) {
    return state;
  }

  const reasonCalls = getReasoningToolCalls(assistantMessage);
  if (
    !reasonCalls.length ||
    hasMatchingReasonRowInLatestSegment(state, reasonCalls)
  ) {
    return [...state, assistantMessage];
  }

  return [
    ...state,
    {
      role: 'reason',
      tool_calls: reasonCalls,
    },
    assistantMessage,
  ];
};

// ─── Conversations Reducer ───────────────────────────────────────────
// Single gate for every conversations state transition. All writers go
// through dispatch instead of calling setConversations directly, which
// eliminates stale-closure overwrites and centralises dedup logic.

const conversationsReducer = (state, action) => {
  switch (action.type) {
    // Full replacement — backend is the source of truth.
    case 'CONVERSATIONS_LOADED':
      return action.payload;

    // Append user message(s) / tool results before streaming starts.
    case 'USER_MESSAGE_SENT':
      return [...state, ...action.payload];

    // Stream finished — append assistant message with dedup guard.
    case 'STREAM_COMPLETED': {
      const { assistantMessage } = action.payload;
      return appendAssistantWithReasoning(state, assistantMessage);
    }

    // Backend completion signal — append with dedup guard.
    case 'COMPLETION_RECEIVED': {
      const { finalAssistantMessage } = action.payload;
      return appendAssistantWithReasoning(state, finalAssistantMessage);
    }

    // Update tool_calls on the last assistant message (approval flow).
    case 'UPDATE_LAST_TOOL_CALLS': {
      const { updatedToolCalls } = action.payload;
      if (!state.length) return state;
      const last = state[state.length - 1];
      if (!last || !last.tool_calls || !last.tool_calls.length) return state;
      const updated = [...state];
      updated[updated.length - 1] = { ...last, tool_calls: updatedToolCalls };
      return updated;
    }

    // Clear — task switch, new task, delete.
    case 'TASK_SWITCHED':
      return [];

    // Persist reasoning/tool rows after the last user message (before assistant response).
    case 'PERSIST_THINKING_ROWS': {
      const { rows } = action.payload;
      if (!rows || !rows.length) return state;
      const updated = [...state];
      let insertIdx = -1;
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].role === 'user') {
          insertIdx = i + 1;
          break;
        }
      }
      if (insertIdx === -1) return [...state, ...rows];
      const rowsToInsert = rows.filter((row) => {
        if (row.role !== 'reason') return true;
        return !hasMatchingReasonRowInLatestSegment(updated, row.tool_calls);
      });
      if (!rowsToInsert.length) return state;
      updated.splice(insertIdx, 0, ...rowsToInsert);
      return updated;
    }

    default:
      return state;
  }
};

export const DataContext = createContext({});

export const DataState = () => {
  const context = useContext(DataContext);
  if (!context) {
    // Return empty object instead of undefined to prevent destructuring errors
    // while the provider is still mounting or if accessed outside.
    return {};
  }
  return context;
};

const DataProvider = ({ children }) => {
  const defaultDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const [theme, setTheme] = useLocalStorage(
    'theme',
    defaultDark ? 'dark' : 'light',
  );

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [conversations, dispatchConversations] = useReducer(
    conversationsReducer,
    [],
  );
  const [input, setInput] = useState('');
  const [isAutomatic, setIsAutomatic] = useState(true);
  const [taskInput, setTaskInput] = useState('');
  // const [currentChat, setCurrentChat] = useState({ role: "user", content: "" });
  const [currentToolResults, setCurrentToolResults] = useState([]);
  const [creatingTask, setCreatingTask] = useState(true);
  const [error, setError] = useState(null);
  const [tools, setTools] = useState([]);

  // New Project Flow
  const [userGoogleId, setUserGoogleId] = useState(
    localStorage.getItem('projectToken') || '',
  ); // ProjectIDToken
  const [justChangedLocalStorageData, setJustChangedLocalStorageData] =
    useState(false);

  const [subtasks, setSubtasks] = useState([]);
  const [showTools, setShowTools] = useState(false);
  const [hasTaskResults, setHasTaskResults] = useState(false); // New state
  const [isBusy, setIsBusy] = useState(false); // New state
  const [steps, setSteps] = useState('');

  const messageContainerRef = useRef(null);
  const [justSavedFiles, setJustSavedFiles] = useState(true);
  const [newTaskSubmitted, setNewTaskSubmitted] = useState(false);

  const [createTaskLoading, setCreateTaskLoading] = useState(false);
  const [sendTaskLoading, setSendTaskLoading] = useState(false);
  const sendSubTaskLoading = false;
  // Boolean state for checking if the currently the model is streaming
  const [isStreaming, setIsStreaming] = useState(false);
  // True while stream ended with tool calls but next cycle hasn't started yet
  const [hasPendingToolCalls, setHasPendingToolCalls] = useState(false);
  const completionEventSourceRef = useRef(null);

  const [streamingResponse, setStreamingResponse] = useState({
    role: 'assistant',
    content: [],
    tool_calls: [],
  });

  const [isCreatingSubTask, setIsCreatingSubTask] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const [bots, setBots] = useState([]);
  const [botsHistory, setBotsHistory] = useState([]);
  const [workflows, setWorkflows] = useState([]); // Graph Workflows State
  const [currentBotSessions, setCurrentBotSessions] = useState([]); // need to check use
  const [currentBotSession, setCurrentBotSession] = useState(null);
  const [currentBotId, setCurrentBotId] = useState(null);
  const [botCreationId, setBotCreationId] = useState(null);
  const [currentBot, setCurrentBot] = useState(null);
  const [clarificationQuestions, setClarificationQuestions] = useState([]);
  const [submitClarificationQuestions, setSubmitClarificationQuestions] =
    useState(false);
  const [needBotFetch, setNeedBotFetch] = useState(true);

  // For managing botstepsmodal
  const [isBotStepsModalOpen, setBotStepsModalOpen] = useState(false); // New state for BotStepsModal
  const [selectedBotId, setSelectedBotId] = useState(null);

  // Bot steps shown in UI
  const [botTaskId, setBotTaskId] = useState(null);
  const [showBotSteps, setShowBotSteps] = useState(false);
  const [justStartedBot, setJustStartedBot] = useState(false);
  const [newTriggerName, setNewTriggerName] = useState('');
  const [botStepInputToSubmit, setBotStepInputToSubmit] = useState({});
  const [useAutomaticStepSwitching, setUseAutomaticStepSwitching] =
    useState(false);
  const [moveToNextStep, setMoveToNextStep] = useState(false);

  // Adding bot chats into normal chats
  const [botSessionJustCreatedId, setBotSessionJustCreatedId] = useState(null);
  const [currentTaskSessions, setCurrentTaskSessions] = useState({});
  const [currentBotSessionChats, setCurrentBotSessionChats] = useState({});
  // const [currentBotSteps, setCurrentBotSteps] = useState([]);
  const [trackExecution, setTrackExecution] = useState(null);

  const [allUserDataSourceFiles, setAllUserDataSourceFiles] = useState([]);
  const [currentTaskDataSourceFiles, setCurrentTaskDataSourceFiles] = useState(
    [],
  );
  const [toolCallApprovalList, setToolCallApprovalList] = useState([]); // Holds all tool calls with approval info

  // Images that are currently set to be sent with next prompt
  const [currentTaskImages, setCurrentTaskImages] = useState([]);

  // Tool Call Status
  const [currentToolCallStatus, setCurrentToolCallStatus] = useState([]);
  const [currentToolCallStatusMap, setCurrentToolCallStatusMap] = useState(
    new Map(),
  );
  // In your DataProvider file...
  const [scrollToToolId, setScrollToToolId] = useState(null);
  const [currentReasoningCalls, setCurrentReasoningCalls] = useState([]);
  const [
    currentReasoningToolStreamingData,
    setCurrentReasoningToolStreamingData,
  ] = useState(null);

  // Right Sidebar Width
  const [rightSidebarWidth, setRightSidebarWidth] = useState(256);

  // Reason Options
  const [selectedReason, setSelectedReason] = useState(null);
  const [isWebSearch, setIsWebSearch] = useState(false);
  const [isReasoning, setIsReasoning] = useState(false);
  const [isCanvas, setIsCanvas] = useState(false);
  const [isAgentMode, setIsAgentMode] = useState(false);
  const [agentSequence, setAgentSequence] = useState(['Orchestrator']);
  const [agentTraversalPaths, setAgentTraversalPaths] = useState([]);
  const [taskMCPConnections, setTaskMCPConnections] = useState([]);
  const [taskOqlousTools, setTaskOqlousTools] = useState([]);
  const [taskManualToolSelection, setTaskManualToolSelection] = useState(false);

  const [currentTaskModel, setCurrentTaskModel] = useState(null);

  // Guard ref: stays true from when auto-tool-use starts until handleStreamSend completes.
  // Unlike sendTaskLoading (which resets between handleFunctionCall and handleStreamSend),
  // this ref bridges the gap so the hasTaskResults effect doesn't re-fire handleStreamSend.
  const isAutoToolCycleActiveRef = useRef(false);

  // SideBar Toggle
  const [toggleSidebar, setToggleSidebar] = useState(true);

  const [currentToolResponseForReference, setCurrentToolResponseForReference] =
    useState({});

  // --- NEW: Add state for the task plan ---
  const [currentTaskPlan, setCurrentTaskPlan] = useState([]);

  // Chat Status Message:
  const [statusMessage, setStatusMessage] = useState(null);

  // Handling Streaming Stop
  const eventSourceRef = useRef(null); // Add this line
  const streamConnectionRef = useRef(null);
  const activeStreamingTaskIdRef = useRef(null);
  const completionListenerTaskIdRef = useRef(null);
  const currentTaskIdRef = useRef(currentTaskId);
  const loadConversationsAbortRef = useRef(null); // Abort in-flight loadConversations on chat switch
  const pendingStreamingRaf = useRef(null); // rAF handle for streaming throttle
  const pendingReasoningRaf = useRef(null); // rAF handle for reasoning tool streaming throttle
  const pendingReasoningData = useRef(null); // holds latest reasoning data between frames
  const resetStreamingUiState = () => {
    setStreamingResponse({
      role: 'assistant',
      content: [],
      tool_calls: [],
      reasoning: '',
    });
    setCurrentReasoningToolStreamingData(null);
    setStatusMessage(null);
    setSendTaskLoading(false);
    setIsStreaming(false);
    setHasPendingToolCalls(false);
  };

  const detachActiveTaskStreams = (nextTaskId = null) => {
    let resetNeeded = false;
    if (
      activeStreamingTaskIdRef.current &&
      activeStreamingTaskIdRef.current !== nextTaskId
    ) {
      if (streamConnectionRef.current) {
        streamConnectionRef.current.onmessage = null;
        streamConnectionRef.current.onerror = null;
        streamConnectionRef.current.onclose = null;
        streamConnectionRef.current.close();
        streamConnectionRef.current = null;
      }
      eventSourceRef.current = null;
      activeStreamingTaskIdRef.current = null;
      resetNeeded = true;
    }

    if (
      completionEventSourceRef.current &&
      completionListenerTaskIdRef.current &&
      completionListenerTaskIdRef.current !== nextTaskId
    ) {
      completionEventSourceRef.current.close();
      completionEventSourceRef.current = null;
      completionListenerTaskIdRef.current = null;
      resetNeeded = true;
    }

    if (resetNeeded) {
      resetStreamingUiState();
    }
  };

  const stopStreaming = async () => {
    if (eventSourceRef.current) {
      try {
        await stopStreamChat(eventSourceRef.current, userGoogleId);
        toast.success('Response generation stopped.'); // Notify the user
      } catch (err) {
        toast.error('Could not stop generation.');
      }
    }
  };

  // Then the effect would run after the component re-renders.
  useEffect(() => {
    const newMap = new Map();
    currentToolCallStatus.forEach((tc) => {
      newMap.set(tc.id, {
        status: tc.status,
        running_phase: tc.running_phase,
        approved: tc.approved,
        approvalNeeded: tc.approvalNeeded,
      });
    });
    setCurrentToolCallStatusMap(newMap);
  }, [currentToolCallStatus]); // The effect depends on the list

  useEffect(() => {
    currentTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);

  useEffect(() => {
    if (!isStreaming && !hasPendingToolCalls) setStatusMessage(null);
  }, [isStreaming, hasPendingToolCalls]);

  useEffect(() => {
    detachActiveTaskStreams(currentTaskId || null);

    if (currentTaskId) {
      dispatchConversations({ type: 'TASK_SWITCHED' });
      setCreatingTask(false);
      setCurrentToolResults([]);
      setToolCallApprovalList([]);
      setCurrentToolCallStatus([]);
      setCurrentReasoningCalls([]);
      setCurrentReasoningToolStreamingData(null);
      setSelectedBotId(null);
      setJustSavedFiles(true);
      setBotTaskId(false);
      setShowBotSteps(false);
      setJustStartedBot(false);
      setNewTriggerName('');
      setStreamingResponse({
        role: 'assistant',
        content: [],
        tool_calls: [],
      });
      setBotStepInputToSubmit({});
      setMoveToNextStep(false);
      setBotSessionJustCreatedId(null);
      setCurrentTaskSessions({});
      setCurrentBotSessionChats({});
      setCurrentToolResponseForReference({}); // New for task reference
      // Logic for getting current task data source files
      // loadConversations(currentTaskId);
      fetchCurrentTaskDataSourceFiles();
    } else {
      setCurrentToolCallStatus([]);
      setCurrentReasoningCalls([]);
      setCurrentReasoningToolStreamingData(null);
      setStreamingResponse({
        role: 'assistant',
        content: [],
        tool_calls: [],
      });
    }
    // eslint-disable-next-line
  }, [currentTaskId]);

  useEffect(() => {
    if (userGoogleId) {
      // Logic for getting all data source files for current user
      fetchDataSourceFiles();
    }
    // eslint-disable-next-line
  }, [userGoogleId]);

  const fetchDataSourceFiles = async () => {
    try {
      const response = await listFilesDataSource(userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        setAllUserDataSourceFiles(response);
      }
    } catch (error) {
      toast.error('Error fetching files');
      setError('Error fetching files');
    }
  };

  const fetchCurrentTaskDataSourceFiles = async () => {
    try {
      const response = await getTaskDataSources(currentTaskId, userGoogleId);
      if (response.error) {
        setError(response.error);
      } else {
        setCurrentTaskDataSourceFiles(response.data_source_ids || []);
      }
    } catch (error) {
      toast.error('Error fetching files');
      setError('Error fetching files');
    }
  };
  useFileStatusPolling(
    allUserDataSourceFiles,
    userGoogleId,
    setAllUserDataSourceFiles,
    setError,
  );

  useEffect(() => {
    if (userGoogleId && justSavedFiles) {
      fetchFiles();
      setJustSavedFiles(false);
    }
    // eslint-disable-next-line
  }, [userGoogleId, justSavedFiles]);

  const closeBotStepsModal = () => {
    setBotStepsModalOpen(false);
    setSelectedBotId(null);
  };

  const navigate = useNavigate();

  const fetchFiles = async () => {
    try {
      const response = await listFiles(userGoogleId, currentTaskId);
      if (response.error) {
        setError(response.error);
      } else {
        setFiles(response);
        setError(null);
      }
    } catch (error) {
      toast.error('Error fetching files');
      setError('Error fetching files');
    }
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem('projectToken');
    const storedUserIdToken = localStorage.getItem('userToken');

    if (!storedUserGoogleId || !storedUserIdToken) {
      navigate('/login');
    } else {
      setUserGoogleId(storedUserGoogleId);
      loadTasks(storedUserGoogleId);
      const getUserInfoFunc = async () => {
        try {
          const response = await getUserInfo(storedUserIdToken);
          if (response.error) {
            setError(response.error);
          }
          setUserInfo(response);
          setError(null);
        } catch (e) {
          toast.error('An error occured while fetching user info');
          setError('An error occured while fetching user info');
        }
      };
      getUserInfoFunc();
    }
    // eslint-disable-next-line
  }, []);

  const loadTasks = async (userGoogleId) => {
    try {
      const tasks = await fetchTasks(userGoogleId);
      setTasks(tasks);
      if (tasks.length > 0) {
        // setCurrentTaskId(tasks[0]._id);
        // loadConversations(tasks[0]._id);
        // setTools(tasks[0].tools);
      }
    } catch (error) {
      toast.error('An error occured while fetching tasks');
      setError('An error occurred while fetching tasks.');
    }
  };

  // BOTS
  useEffect(() => {
    if (needBotFetch && userGoogleId) {
      setNeedBotFetch(false);
      loadBots(userGoogleId);
      loadBotsHistory(userGoogleId);
      fetchWorkflows(userGoogleId);
      // setNeedBotFetch(false);
    }
  }, [needBotFetch, userGoogleId]);
  // useEffect(() => {
  //   if (submitClarificationQuestions && userGoogleId && botCreationId){
  //     submitClarificationAnswersAndCreateBotSteps();
  //     setSubmitClarificationQuestions(false);
  //   }
  // }, [submitClarificationQuestions])

  const loadBots = async (userGoogleId) => {
    try {
      const temp_bots = await fetchBots(userGoogleId);
      setBots(temp_bots);
      // setNeedBotFetch(false);
    } catch (error) {
      toast.error('An error occured while fetching tasks');
      setError('An error occurred while fetching tasks.');
    }
  };
  async function fetchWorkflows(userGoogleId) {
    try {
      const data = await getWorkflows(userGoogleId); // Update the endpoint as needed
      //   const data = await response.json();
      setWorkflows(data);
    } catch (error) {
      toast.error(
        typeof error === 'object' ? error.message : JSON.stringify(error),
      );
    }
  }

  const loadBotsHistory = async (userGoogleId) => {
    try {
      const temp_bots_history = await fetchBotsHistory(userGoogleId);
      setBotsHistory(temp_bots_history);
    } catch (error) {
      toast.error('Error:', error);
      setError('An error occurred while fetching tasks.');
    }
  };

  // Function to create a task bot and handle clarification questions
  const createAndHandleClarificationQuestions = async (
    instruction,
    name,
    trigger,
    apps,
    dataSources,
  ) => {
    try {
      // Create the task bot
      // Extract file IDs to send to the API
      // const selectedFileIds = currentTaskDataSourceFiles.map(
      //   (file) => file.file_id
      // );
      const selectedFileIds = currentTaskDataSourceFiles;

      const botResponse = await createTaskBot(
        {
          task: instruction,
          name: name === '' ? 'Untitled' : name,
          trigger,
          apps,
          bot_id: botCreationId,
          // data_sources: dataSources,
          selected_data_source_file_ids: selectedFileIds,
        },
        userGoogleId,
      );

      if (botResponse.error) {
        throw new Error(botResponse.error);
      }

      // Save the current bot ID and details
      setBotCreationId(botResponse.bot_id);
      // setClarificationQuestions(botResponse.questions)
      // setCurrentBot(botResponse.bot);

      // Check if there are any clarification questions
      if (botResponse.questions && botResponse.questions.length > 0) {
        // Save the clarification questions in the state
        // Transform the list of questions into a list of objects with question and answer keys
        const formattedQuestions = botResponse.questions.map((question) => ({
          id: question.id,
          question: question.question,
          answer: '',
        }));

        // Save the clarification questions in the state
        setClarificationQuestions(formattedQuestions);

        // setClarificationQuestions(botResponse.questions);
      }

      return botResponse;
    } catch (error) {
      toast.error(
        'Error in creating task bot and handling clarification questions',
      );

      throw error;
    }
  };

  // Function to submit clarification answers and create bot steps
  const submitClarificationAnswersAndCreateBotSteps = async () => {
    try {
      if (!botCreationId || !clarificationQuestions || !userGoogleId) {
        setError('Can not complete the request');
        return;
      }
      setIsBusy(true);
      // Prepare the data to be sent for creating bot steps
      const stepsData = {
        bot_id: botCreationId,
        clarification_questions: clarificationQuestions,
        // Add any additional data needed for creating bot steps
      };

      // Submit the answers and create the bot steps
      const stepsResponse = await createBotSteps(stepsData, userGoogleId);

      // Optionally, you can handle the response further, such as updating the UI or state

      setIsBusy(false);
      return stepsResponse;
    } catch (error) {
      toast.error(
        'Error in submitting clarification answers and creating bot steps',
      );

      setIsBusy(false);
      throw error;
    }
  };

  useEffect(() => {
    if (newTaskSubmitted) {
      handleStreamSend();
      setNewTaskSubmitted(false);
    }
    // eslint-disable-next-line
  }, [newTaskSubmitted]);

  useEffect(() => {
    setToolCallApprovalList([]);

    const updateCurrentAttachmentTaskFiles = () => {
      const fileIdsInConversations = conversations
        .filter((chat) => chat.role === 'user' && chat.attachments)
        .flatMap((chat) => chat.attachments.map((att) => att.file_id));
      if (fileIdsInConversations.length > 0) {
        setFiles((prevFiles) =>
          prevFiles.filter(
            (file) => !fileIdsInConversations.includes(file.file_id),
          ),
        );
      }
    };
    const handleAutomaticToolUse = async () => {
      // Skip automatic tool use while a stream or send is already in progress
      // to prevent re-triggering handleStreamSend from stale conversation state.
      if (sendTaskLoading || isStreaming || isAutoToolCycleActiveRef.current)
        return;

      if (isAutomatic && !selectedBotId && conversations.length) {
        const lastChat = conversations[conversations.length - 1];
        if (lastChat && lastChat.tool_calls && lastChat.tool_calls.length > 0) {
          // Mark the entire auto-tool cycle as active. This stays true until
          // handleStreamSend (triggered by hasTaskResults) sets it back to false.
          isAutoToolCycleActiveRef.current = true;

          // Tag each tool with its approval/file-upload requirements
          const updatedToolCalls = lastChat.tool_calls.map((toolCall) => {
            let approvalNeeded = functionsNeedingApproval.has(
              toolCall.function.name,
            );
            // Action-specific approval check (e.g. confluence_manage_pages only when action='create'/'update'/'delete')
            if (!approvalNeeded) {
              try {
                const args =
                  typeof toolCall.function.arguments === 'string'
                    ? JSON.parse(toolCall.function.arguments)
                    : toolCall.function.arguments || {};
                const actionKey = `${toolCall.function.name}__${args.action}`;
                if (functionsNeedingApprovalForAction.has(actionKey)) {
                  approvalNeeded = true;
                }
              } catch {
                // malformed arguments — skip action check
              }
            }
            // Standard file-upload check (whole tool name)
            let fileUploadNeeded = functionsNeedingFileUpload.has(
              toolCall.function.name,
            );
            // Action-specific file-upload check (e.g. jira_manage_issue only when action='attach_file')
            if (!fileUploadNeeded) {
              try {
                const args =
                  typeof toolCall.function.arguments === 'string'
                    ? JSON.parse(toolCall.function.arguments)
                    : toolCall.function.arguments || {};
                const actionKey = `${toolCall.function.name}__${args.action}`;
                if (functionsNeedingFileUploadForAction.has(actionKey)) {
                  // For attach_file, if the AI already provided a file path (e.g., from fetch_user_files),
                  // we don't need to ask the user to upload one. We only pause for upload if the array is empty or missing.
                  const filePaths = args.payload?.file_paths;
                  if (
                    !filePaths ||
                    (Array.isArray(filePaths) && filePaths.length === 0)
                  ) {
                    fileUploadNeeded = true;
                  }
                }
              } catch {
                // malformed arguments — skip action check
              }
            }
            return {
              ...toolCall,
              approvalNeeded: approvalNeeded ? true : false,
              approved: approvalNeeded && !toolCall.approved ? false : true,
              fileUploadNeeded: fileUploadNeeded ? true : false,
              fileUploaded:
                fileUploadNeeded && !toolCall.fileUploaded ? false : true,
            };
          });

          // Separate tools into buckets
          const readyToExecute = updatedToolCalls.filter(
            (tc) =>
              (!tc.approvalNeeded || tc.approved) &&
              (!tc.fileUploadNeeded || tc.fileUploaded) &&
              !tc.disapproved,
          );
          const disapprovedTools = updatedToolCalls.filter(
            (tc) => tc.disapproved,
          );
          const stillPending = updatedToolCalls.filter(
            (tc) =>
              (tc.approvalNeeded && !tc.approved) ||
              (tc.fileUploadNeeded && !tc.fileUploaded),
          );

          // If nothing is ready yet — show the approval UI and wait
          if (readyToExecute.length === 0 && stillPending.length > 0) {
            setToolCallApprovalList(updatedToolCalls);
            isAutoToolCycleActiveRef.current = false;
            return;
          }

          // If file upload is needed for any pending tool, show the upload UI
          const needsFileUpload = updatedToolCalls.some(
            (tc) => tc.fileUploadNeeded && !tc.fileUploaded,
          );
          if (needsFileUpload && readyToExecute.length === 0) {
            setToolCallApprovalList(updatedToolCalls);
            isAutoToolCycleActiveRef.current = false;
            return;
          }

          // Execute only the tools that are currently approved/ready
          let tool_responses = [];
          const toolsToRun = readyToExecute;

          try {
            const responses = await Promise.all(
              toolsToRun.map(async (toolCall) => {
                const data = await handleFunctionCall(toolCall);
                if (!data) {
                  throw new Error('Failed to handle tool call');
                }

                try {
                  const contentObj =
                    typeof data.content === 'string'
                      ? JSON.parse(data.content)
                      : data.content;
                  const obs = contentObj?.observation;
                  const nextAct = contentObj?.next_recommended_action;
                  if (obs) {
                    const syntheticReflection = {
                      id: `reflection-${toolCall.id}`,
                      type: 'function',
                      status: 'reasoning',
                      function: {
                        name: 'record_thought_process',
                        arguments: JSON.stringify({
                          phase: 'reflecting',
                          reasoning_text: `Tool "${toolCall.function.name}" ${data.is_error ? 'failed' : 'completed'}.\n\nObservation: ${obs}${nextAct ? `\n\nRecommended next action: ${nextAct}` : ''}`,
                          confidence: data.is_error ? 0.3 : 0.8,
                          next_action: nextAct || null,
                        }),
                      },
                    };
                    setCurrentReasoningCalls((prev) => [
                      ...prev,
                      syntheticReflection,
                    ]);
                  }
                } catch {
                  // content not parseable, skip reflection injection
                }

                return { ...data, functionName: toolCall.function.name };
              }),
            );

            tool_responses.push(...responses);
          } catch (error) {
            isAutoToolCycleActiveRef.current = false;
            return;
          }

          // Also send disapproved tools as tool results (backend needs them to continue)
          const disapprovedResponses = disapprovedTools.map((tc) => ({
            id: tc.id,
            content: JSON.stringify({
              error: tc.disapprovalReason || 'Declined by user',
            }),
            functionName: tc.function.name,
            approved: true,
            disapproved: true,
          }));

          const allResponses = [...tool_responses, ...disapprovedResponses];

          if (allResponses.length > 0) {
            // If there are still pending tools, keep the approval UI open
            if (stillPending.length > 0) {
              setToolCallApprovalList(stillPending);
              isAutoToolCycleActiveRef.current = false;
            }

            const modified_tool_responses = allResponses.map(
              (tool_response) => ({ ...tool_response, approved: true }),
            );
            setCurrentToolResults(modified_tool_responses);
          }
        }
      }
    };

    handleAutomaticToolUse();
    updateCurrentAttachmentTaskFiles();
    // eslint-disable-next-line
  }, [conversations, isAutomatic]);

  useEffect(() => {
    if (
      currentToolResults.length > 0 &&
      currentToolResults.every((tool_response) => tool_response.approved)
    ) {
      setHasTaskResults(true);
    }
  }, [currentToolResults]);

  useEffect(() => {
    if (hasTaskResults && isAutomatic && !sendTaskLoading && !isStreaming) {
      // Clear the auto-tool cycle guard — handleStreamSend will set its own
      // sendTaskLoading/isStreaming flags which protect against re-entry.
      isAutoToolCycleActiveRef.current = false;
      handleStreamSend();
      setHasTaskResults(false); // Reset the state
    }
    // eslint-disable-next-line
  }, [hasTaskResults]);

  /**
   * Processes conversations to add a status to each tool call in the original
   * assistant messages and aggregates them for UI display.
   * @param {Array<Object>} conversations The list of conversation messages.
   * @returns {{
   * finalConversations: Array<Object>,
   * pendingToolCalls: Array<Object>,
   * pendingReasonCalls: Array<Object>
   * }}
   */
  const addToolCallUI = (conversations) => {
    // Create a deep copy to safely add properties to the original message objects.
    const updatableConversations = structuredClone(conversations);

    // A map to link a tool_call_id to its corresponding tool_call object reference.
    const toolCallMap = new Map();

    // First, pass through the copied conversations to update tool_call statuses in place.
    for (const msg of updatableConversations) {
      // If it's an assistant message, set the initial status and map the calls for lookup.
      if (msg.role === 'assistant' && msg.tool_calls) {
        for (const tc of msg.tool_calls) {
          tc.status = 'running'; // Set initial status
          toolCallMap.set(tc.id, tc); // Map the object reference for future updates
        }
      }
      // If it's a tool response, find the original call in the map and update its status.
      else if (msg.role === 'tool') {
        const originalToolCall = toolCallMap.get(msg.tool_call_id);
        if (originalToolCall) {
          originalToolCall.status = msg.is_error ? 'failed' : 'success';
        }
      }
    }

    // Now, run the aggregation logic on the conversations that have updated statuses.
    const finalConversations = [];
    let pendingToolCalls = [];
    let pendingReasonCalls = [];
    let i = 0;

    while (i < updatableConversations.length) {
      const msg = updatableConversations[i];
      // Always push the (now modified) message.
      finalConversations.push(msg);

      if (msg.role === 'user') {
        // Start a new segment after a user prompt.
        let aggregatedToolCalls = [];
        let reasonCalls = [];
        let j = i + 1;

        // Collect tool call data until the next user prompt.
        while (
          j < updatableConversations.length &&
          updatableConversations[j].role !== 'user'
        ) {
          const nextMsg = updatableConversations[j];

          if (nextMsg.role === 'assistant' && nextMsg.tool_calls?.length > 0) {
            // The status is already on the tool_call object, so we just filter and add.
            aggregatedToolCalls.push(
              ...nextMsg.tool_calls.filter(
                (tc) => tc.function?.name !== 'record_thought_process',
              ),
            );

            nextMsg.tool_calls.forEach((tc) => {
              if (tc.function?.name === 'record_thought_process') {
                reasonCalls.push(tc);
              }
            });
          }
          j++;
        }

        // If we encountered another user message (i.e. segment complete), add the aggregated UI messages.
        if (
          j < updatableConversations.length &&
          aggregatedToolCalls.length > 0
        ) {
          if (reasonCalls.length) {
            finalConversations.push({
              role: 'reason',
              tool_calls: reasonCalls,
            });
          }
          finalConversations.push({
            role: 'tool_calls_ui',
            tool_calls: aggregatedToolCalls,
          });
        } else {
          // Otherwise, these tool calls are still pending.
          pendingToolCalls = aggregatedToolCalls;
          pendingReasonCalls = reasonCalls;
        }
      }
      i++;
    }

    return { finalConversations, pendingToolCalls, pendingReasonCalls };
  };

  const loadConversations = async (taskId) => {
    // Abort any in-flight loadConversations fetch from a previous chat switch
    // so it doesn't resolve and overwrite the new chat's state.
    if (loadConversationsAbortRef.current) {
      loadConversationsAbortRef.current.abort();
    }
    const abortController = new AbortController();
    loadConversationsAbortRef.current = abortController;

    try {
      // setTrackExecution("step-2");
      setIsBusy(true);
      const { chats, streaming_status } = await fetchTaskConversations(
        taskId,
        userGoogleId,
        abortController.signal,
      );

      // If this request was aborted (user switched chats again), bail out
      // so we don't overwrite the newer chat's conversations.
      if (abortController.signal.aborted) return;

      let conversations = chats;

      // setCurrentChat({ role: "user", content: [] });
      setCurrentToolResults([]);

      // Process the conversations to add the aggregated "tool_calls_ui" messages.
      const { finalConversations, pendingToolCalls, pendingReasonCalls } =
        addToolCallUI(conversations);

      // Set the final aggregated conversations while keeping the original messages intact.
      dispatchConversations({
        type: 'CONVERSATIONS_LOADED',
        payload: finalConversations,
      });

      if (streaming_status?.is_streaming) {
        setIsStreaming(true);
        setSendTaskLoading(true);
        listenForCompletion(taskId, streaming_status.stream_id);
      } else {
        if (
          completionEventSourceRef.current &&
          completionListenerTaskIdRef.current === taskId
        ) {
          completionEventSourceRef.current.close();
          completionEventSourceRef.current = null;
          completionListenerTaskIdRef.current = null;
        }
        setIsStreaming(false);
        setSendTaskLoading(false);
      }

      // Set pending tool calls (those without a following user prompt) in a separate state.
      setCurrentToolCallStatus(pendingToolCalls);
      setCurrentReasoningCalls(pendingReasonCalls);

      // --- CORRECTED LOGIC: Set the taskPlan state directly from finalConversations ---
      const lastMessageWithPlan = [...finalConversations]
        .reverse()
        .find((msg) => msg.plan && msg.plan.goals);

      // Set the plan state based on the last message found
      setCurrentTaskPlan(
        lastMessageWithPlan ? lastMessageWithPlan.plan.goals : [],
      );
      // --- END OF CORRECTED LOGIC ---

      finalConversations.forEach((msg) => {
        if (msg && msg.role === 'tool' && typeof msg.content === 'string') {
          extractAndSetToolResponsesForReferencing(
            msg,
            setCurrentToolResponseForReference,
          );
        }
      });

      const newSubtasks = conversations.filter((chat) => chat.isSubtask); // Local variable to hold the new subtasks
      setSubtasks(newSubtasks);
    } catch (error) {
      // Silently ignore aborted requests — the user simply switched chats.
      if (error?.name === 'AbortError' || abortController.signal.aborted)
        return;
      toast.error('An error occurred while fetching conversations');
      setError('An error occurred while fetching conversations.');
    } finally {
      // Only clear busy state if this is still the active request
      if (!abortController.signal.aborted) {
        setIsBusy(false);
      }
    }
  };

  // In DataProvider.js
  const listenForCompletion = (taskId, streamId) => {
    if (completionEventSourceRef.current) {
      completionEventSourceRef.current.close();
    }
    const eventSource = completeStreamChat(streamId, userGoogleId);
    completionEventSourceRef.current = eventSource;
    completionListenerTaskIdRef.current = taskId;

    const handleFinalSignal = (event) => {
      try {
        const payload = JSON.parse(event.data);

        if (payload.status === 'done') {
          const { final_assistant_message } = payload;
          toast.success('New message received!');
          if (currentTaskIdRef.current === taskId) {
            if (currentReasoningCalls.length > 0) {
              dispatchConversations({
                type: 'PERSIST_THINKING_ROWS',
                payload: {
                  rows: [{ role: 'reason', tool_calls: currentReasoningCalls }],
                },
              });
            }

            dispatchConversations({
              type: 'COMPLETION_RECEIVED',
              payload: { finalAssistantMessage: final_assistant_message },
            });

            setCurrentToolCallStatus([]);
            setCurrentReasoningCalls([]);
            setCurrentReasoningToolStreamingData(null);
            setStreamingResponse({
              role: 'assistant',
              content: [],
              tool_calls: [],
            });
          }
        } else {
          if (currentTaskIdRef.current === taskId) {
            toast.error(
              payload.message ||
                'An error occurred while generating the response.',
            );
          }
        }
      } catch (error) {
        // loadConversations(currentTaskId);
      } finally {
        // Clean up everything
        eventSource.close();
        completionEventSourceRef.current = null;
        completionListenerTaskIdRef.current = null;
        if (currentTaskIdRef.current === taskId) {
          setIsStreaming(false);
          setSendTaskLoading(false);
        }
      }
    };

    eventSource.addEventListener('done', handleFinalSignal);
    eventSource.addEventListener('error', handleFinalSignal);

    eventSource.onerror = (err) => {
      eventSource.close();
      completionEventSourceRef.current = null;
      completionListenerTaskIdRef.current = null;
      if (currentTaskIdRef.current === taskId) {
        setIsStreaming(false);
        setSendTaskLoading(false);
      }
      // Fallback: If the connection just drops, a refresh is the safest way to get the final state.
      // loadConversations(currentTaskId);
    };
  };

  useEffect(() => {
    if (userInfo) {
      const available_models = userInfo.available_models || [];
      if (currentTaskModel && !available_models.includes(currentTaskModel)) {
        // If the current model is not available, you can handle it here
        setCurrentTaskModel(null);
      }
    }
    // eslint-disable-next-line
  }, [userInfo]);

  const handleStreamSend = async (
    subtaskTools = null,
    explicitInput = null,
  ) => {
    // Use explicitInput if provided, otherwise fallback to the input state
    const currentInputText = explicitInput !== null ? explicitInput : input;
    const streamingTaskId = currentTaskId;

    if (!currentInputText.trim() && !currentToolResults.length) return;
    activeStreamingTaskIdRef.current = streamingTaskId;
    // if (!currentInputText.trim() && !currentChat.content.length) return;
    setSendTaskLoading(true);
    setIsStreaming(true);
    setHasPendingToolCalls(false);
    setTrackExecution('step-2');

    // ================= STATUS MESSAGE =================
    setStatusMessage('Processing...');
    let firstChunkReceived = false;
    const statusTimers = [];

    const rand = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    const scheduleStatusUpdate = (delay, text) => {
      const t = setTimeout(() => {
        if (!firstChunkReceived) setStatusMessage(text);
      }, delay);
      statusTimers.push(t);
    };

    let cumulativeDelay = 0;
    cumulativeDelay += rand(1000, 2500);
    scheduleStatusUpdate(cumulativeDelay, 'Preparing context...');
    cumulativeDelay += rand(3000, 6000);
    scheduleStatusUpdate(cumulativeDelay, 'Planning...');

    const clearStatus = () => {
      statusTimers.forEach(clearTimeout);
      statusTimers.length = 0;
      firstChunkReceived = true;
      // statusMessage is cleared by the isStreaming=false effect, not here,
      // so the gate condition in ChatList bridges the gap until content appears.
    };

    // ================= MAIN LOGIC =================
    try {
      setSelectedBotId(null);
      setError('');

      let newChat = []; // Local variable to hold the new chat
      if (currentInputText.trim()) {
        const currentAttachedTaskFiles = files.filter(
          (file) => file.is_attachment,
        );

        setInput('');
        newChat = [
          {
            role: 'user',
            content: [{ type: 'text', text: currentInputText.trim() }],
            attachments: currentAttachedTaskFiles,
          },
        ];

        if (currentTaskImages.length > 0) {
          const newContent = [...newChat[0].content];

          for (const image of currentTaskImages) {
            newContent.push({
              type: 'image_url',
              image_url: { url: image.url },
              image_id: image.id,
            });
          }

          newChat[0].content = newContent;
        }
      }

      let updatedConversations = [...conversations];

      if (currentToolResults.length) {
        newChat = [...currentToolResults];
      } else {
        if (
          currentToolCallStatus.length > 0 ||
          currentReasoningCalls.length > 0
        ) {
          const thinkingRows = [];

          if (currentReasoningCalls.length > 0) {
            thinkingRows.push({
              role: 'reason',
              tool_calls: currentReasoningCalls,
            });
          }

          if (currentToolCallStatus.length > 0) {
            thinkingRows.push({
              role: 'tool_calls_ui',
              tool_calls: currentToolCallStatus,
            });
          }

          dispatchConversations({
            type: 'PERSIST_THINKING_ROWS',
            payload: { rows: thinkingRows },
          });

          setCurrentToolCallStatus([]);
          setCurrentReasoningCalls([]);
        }
      }

      updatedConversations = [...updatedConversations, ...newChat];

      dispatchConversations({ type: 'USER_MESSAGE_SENT', payload: newChat });
      setCurrentTaskImages([]);
      setCurrentToolResults([]);

      // ================= TASK UPDATE =================
      setTasks((prevTasks) => {
        const taskIndex = prevTasks.findIndex((t) => t._id === currentTaskId);
        if (taskIndex === -1) return prevTasks;

        const updatedTask = {
          ...prevTasks[taskIndex],
          reasoning_effort: selectedReason,
          is_web_search: isWebSearch,
          is_reasoning: isReasoning,
          is_canvas: isCanvas,
          is_agent_mode: isAgentMode,
          agent_sequence: agentSequence,
          mcp_connections: taskMCPConnections,
          oqlous_tools: taskOqlousTools,
          manual_tool_selection: taskManualToolSelection,
          task_model: currentTaskModel,
        };

        return [
          updatedTask,
          ...prevTasks.filter((_, idx) => idx !== taskIndex),
        ];
      });

      // ================= API =================
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const streamInitResponse = await initializeStreamChat(
        {
          tools,
          chats: updatedConversations,
          task_id: currentTaskId,
          reasoning_effort: selectedReason,
          is_web_search: isWebSearch,
          is_canvas: isCanvas,
          is_agent_mode: isAgentMode,
          is_reasoning: isReasoning,
          mcp_connections: taskMCPConnections,
          oqlous_tools: taskOqlousTools,
          manual_tool_selection: taskManualToolSelection,
          task_model: currentTaskModel,
          timezone,
        },
        userGoogleId,
      );
      const streamId = streamInitResponse?.stream_id;
      if (!streamId) {
        throw new Error('Failed to initialize stream. Please try again.');
      }

      if (currentTaskIdRef.current !== streamingTaskId) {
        activeStreamingTaskIdRef.current = null;
        resetStreamingUiState();
        return;
      }

      const eventSource = streamChat(streamId, userGoogleId);
      streamConnectionRef.current = eventSource;
      eventSourceRef.current = streamId;
      activeStreamingTaskIdRef.current = streamingTaskId;

      let toolCallsByIndex = new Map();

      let currentStreamingResponseMessage = {
        role: 'assistant',
        tool_calls: [],
        content: [],
        reasoning: '',
        is_reasoning: false,
      };

      let isConnectionClosed = false;

      // RAF THROTTLE HELPER
      // Mutate currentStreamingResponseMessage freely; this queues
      // exactly ONE setStreamingResponse call per animation frame.
      const scheduleStreamingUpdate = () => {
        if (pendingStreamingRaf.current !== null) return; // already queued
        pendingStreamingRaf.current = requestAnimationFrame(() => {
          pendingStreamingRaf.current = null;
          setStreamingResponse({ ...currentStreamingResponseMessage });
        });
      };

      const flushStreamingUpdate = () => {
        if (pendingStreamingRaf.current !== null) {
          cancelAnimationFrame(pendingStreamingRaf.current);
          pendingStreamingRaf.current = null;
        }
        setStreamingResponse({ ...currentStreamingResponseMessage });
      };

      // RAF THROTTLE HELPER — reasoning tool streaming data
      // Same pattern: buffer the latest data and flush once per frame.
      const scheduleReasoningUpdate = (data) => {
        pendingReasoningData.current = data;
        if (pendingReasoningRaf.current !== null) return; // already queued
        pendingReasoningRaf.current = requestAnimationFrame(() => {
          pendingReasoningRaf.current = null;
          if (pendingReasoningData.current !== null) {
            setCurrentReasoningToolStreamingData(pendingReasoningData.current);
          }
        });
      };

      const flushReasoningUpdate = () => {
        if (pendingReasoningRaf.current !== null) {
          cancelAnimationFrame(pendingReasoningRaf.current);
          pendingReasoningRaf.current = null;
        }
        if (pendingReasoningData.current !== null) {
          setCurrentReasoningToolStreamingData(pendingReasoningData.current);
          pendingReasoningData.current = null;
        }
      };

      // ================= STREAM =================
      eventSource.onmessage = async (event) => {
        if (currentTaskIdRef.current !== streamingTaskId) {
          return;
        }

        if (!firstChunkReceived) {
          clearStatus();
          firstChunkReceived = true;
        }

        setTrackExecution(null);

        let chunk;
        try {
          chunk = JSON.parse(event.data);
        } catch {
          return;
        }

        if (!chunk?.choices) return;

        for (const choice of chunk.choices) {
          const content =
            choice.delta?.content || choice.delta?.reasoning_content || '';

          if (content) {
            currentStreamingResponseMessage.is_reasoning = false;

            if (!currentStreamingResponseMessage.content.length) {
              currentStreamingResponseMessage.content = [
                { type: 'text', text: content },
              ];
            } else {
              currentStreamingResponseMessage.content[0].text += content;
            }

            scheduleStreamingUpdate();
          }

          if (choice.delta?.reasoning) {
            currentStreamingResponseMessage.is_reasoning = true;
            currentStreamingResponseMessage.reasoning += choice.delta.reasoning;

            scheduleStreamingUpdate();
          }

          if (choice.delta?.tool_calls) {
            /* eslint-disable no-loop-func */
            choice.delta.tool_calls.forEach((toolCall) => {
              const index = toolCall.index;
              if (index === undefined) return;

              if (!toolCallsByIndex.has(index)) {
                toolCallsByIndex.set(index, {
                  index,
                  id: toolCall.id || '',
                  function: { arguments: '', name: '' },
                  type: 'function',
                });
              }

              const existing = toolCallsByIndex.get(index);

              if (toolCall.function?.name)
                existing.function.name = toolCall.function.name;

              if (toolCall.function?.arguments) {
                existing.function.arguments += toolCall.function.arguments;

                if (existing.function.name === 'record_thought_process') {
                  scheduleReasoningUpdate({
                    id: existing.id,
                    type: 'function',
                    status: 'reasoning',
                    function: {
                      name: 'record_thought_process',
                      arguments: existing.function.arguments,
                    },
                  });
                }
              }

              currentStreamingResponseMessage.tool_calls = Array.from(
                toolCallsByIndex.values(),
              )
                .sort((a, b) => a.index - b.index)
                .map((tc) => ({
                  id: tc.id,
                  function: tc.function,
                  type: 'function',
                }));

              scheduleStreamingUpdate();
            });
            /* eslint-enable no-loop-func */
          }
        }
      };

      // ================= STREAM END =================
      eventSource.addEventListener('streamEnd', async (event) => {
        if (isConnectionClosed) return;
        isConnectionClosed = true;

        clearStatus();

        try {
          const data = JSON.parse(event.data);
          toolCallsByIndex = new Map(
            Object.entries(data.tool_calls_by_index || {}),
          );

          currentStreamingResponseMessage.content =
            data.current_streaming_response?.content || null;

          currentStreamingResponseMessage.reasoning =
            data.current_streaming_response?.reasoning || null;
        } catch (err) {
          toast.error('Error parsing final stream data: ' + err);
        }

        // Flush any pending rAF update synchronously before closing
        flushStreamingUpdate();
        flushReasoningUpdate();

        eventSource.close();
        streamConnectionRef.current = null;
        eventSourceRef.current = null;
        activeStreamingTaskIdRef.current = null;

        await handleStreamedResponse(
          newChat,
          currentStreamingResponseMessage,
          toolCallsByIndex,
          updatedConversations,
          streamingTaskId,
        );
      });

      // ================= ERROR =================
      eventSource.onerror = (event) => {
        clearStatus();

        const err = JSON.parse(event?.data || '{}');

        eventSource.close();
        streamConnectionRef.current = null;
        eventSourceRef.current = null;
        activeStreamingTaskIdRef.current = null;

        // Cancel any pending RAF updates
        if (pendingStreamingRaf.current !== null) {
          cancelAnimationFrame(pendingStreamingRaf.current);
          pendingStreamingRaf.current = null;
        }
        if (pendingReasoningRaf.current !== null) {
          cancelAnimationFrame(pendingReasoningRaf.current);
          pendingReasoningRaf.current = null;
        }
        pendingReasoningData.current = null;

        toast.error(
          typeof err === 'string' ? err : err?.error || JSON.stringify(err),
        );

        if (currentTaskIdRef.current === streamingTaskId) {
          setSendTaskLoading(false);
          setIsStreaming(false);
        }
      };

      eventSource.onclose = () => {
        clearStatus();
        streamConnectionRef.current = null;
        eventSourceRef.current = null;
        activeStreamingTaskIdRef.current = null;

        if (currentTaskIdRef.current === streamingTaskId) {
          setSendTaskLoading(false);
          setIsStreaming(false);
        }
      };
    } catch (err) {
      clearStatus();
      if (activeStreamingTaskIdRef.current === streamingTaskId) {
        activeStreamingTaskIdRef.current = null;
      }
      streamConnectionRef.current = null;
      eventSourceRef.current = null;

      const errMsg = err?.message || 'An error occurred. Please try again.';
      setError(errMsg);
      toast.error(errMsg);

      if (currentTaskIdRef.current === streamingTaskId) {
        setSendTaskLoading(false);
        setIsStreaming(false);
      }
    }
  };

  const handleStreamedResponse = async (
    newChat,
    currentStreamingResponseMessage,
    toolCallsByIndex,
    updatedConversations,
    streamTaskId,
  ) => {
    currentStreamingResponseMessage.is_reasoning = false;
    currentStreamingResponseMessage.tool_calls = Array.from(
      toolCallsByIndex.values(),
    )
      .sort((a, b) => a.index - b.index) // Sort by index for consistent ordering
      .map((toolCall) => ({
        id: toolCall.id, // Assuming id might be present in some chunks, but index is primary
        function: {
          arguments: toolCall.function.arguments,
          name: toolCall.function.name,
        },
        status: 'running', // Set status to running for UI consistency
        type: 'function',
      }));
    if (currentTaskIdRef.current === streamTaskId) {
      dispatchConversations({
        type: 'STREAM_COMPLETED',
        payload: { assistantMessage: currentStreamingResponseMessage },
      });
      setCurrentToolCallStatus((prevToolStatus) => [
        ...prevToolStatus,
        ...(currentStreamingResponseMessage?.tool_calls || [])
          .map((tool_call) => {
            return { ...tool_call, status: 'running' };
          })
          .filter(
            (tool_call) =>
              tool_call.function?.name !== 'record_thought_process',
          ),
      ]);
      setCurrentReasoningCalls([]);
      setCurrentReasoningToolStreamingData(null);
      setStreamingResponse({
        role: 'assistant',
        content: [],
        tool_calls: [],
        reasoning: '',
      });

      const hasAnyToolCalls =
        currentStreamingResponseMessage.tool_calls.length > 0;
      setHasPendingToolCalls(hasAnyToolCalls);
      setSendTaskLoading(false);
      setIsStreaming(false);

      // Move setError(null) inside the guard to avoid clearing errors from a newly switched task
      setError(null);
    }
  };

  const handleCreateTask = () => {
    setCreatingTask(true);
    setSteps('');
    setCurrentTaskId('');
    setCurrentTaskDataSourceFiles([]);
    dispatchConversations({ type: 'TASK_SWITCHED' });
    setSelectedBotId(null);
    setJustSavedFiles(true);
    setBotTaskId(false);
    setShowBotSteps(false);
    setJustStartedBot(false);
    setNewTriggerName('');
    setBotStepInputToSubmit({});
    setMoveToNextStep(false);
    setBotSessionJustCreatedId(null);
    setCurrentTaskSessions({});
    setCurrentBotSessionChats({});
    setCurrentToolResponseForReference({}); // new for tool response reference
  };

  const handleSubmitTask = (taskInputParam = null, explicitInput = null) => {
    // If explicitInput is provided, use it (from AutoResizableTextarea),
    // otherwise fallback to taskInputParam, otherwise fallback to taskInput state
    const currentInputText =
      explicitInput !== null
        ? explicitInput
        : typeof taskInputParam === 'string'
          ? taskInputParam
          : taskInput;

    if (currentInputText.trim()) {
      setTrackExecution('step-1');
      // scrollToBottom();
      setCreateTaskLoading(true);
      setIsBusy(true);
      createTask(
        {
          task: currentInputText,
          reasoning_effort: selectedReason,
          is_web_search: isWebSearch,
          is_reasoning: isReasoning,
          is_canvas: isCanvas,
          is_agent_mode: isAgentMode,
          agent_sequence: agentSequence,
          mcp_connections: taskMCPConnections,
          oqlous_tools: taskOqlousTools,
          manual_tool_selection: taskManualToolSelection,
          task_model: currentTaskModel,
        },
        userGoogleId,
      )
        .then(async (data) => {
          if (data.error) {
            setError(data.error);
          } else {
            const newTasks = [
              {
                _id: data.task_id,
                task_description: currentInputText,
                tools: data.tools,
              },
              ...tasks,
            ]; // Local variable to hold the new tasks
            const selectedDataSourceFileIds = currentTaskDataSourceFiles;
            // const selectedDataSourceFileIds = currentTaskDataSourceFiles.map(
            //   (file) => file.file_id
            // );
            const result = await updateTaskDataSources(
              data.task_id,
              userGoogleId,
              selectedDataSourceFileIds,
            );
            if (result.error) {
              throw new Error(result.error);
            }
            setTasks(newTasks);

            setTools(data.tools);
            dispatchConversations({ type: 'TASK_SWITCHED' });

            setInput(currentInputText);
            setCurrentTaskId(data.task_id);
            setNewTaskSubmitted(true);

            setTaskInput('');
            setCreatingTask(false);
            setError(null);
            // scrollToBottom();
            setCreateTaskLoading(false);
          }
        })
        .catch((error) => {
          toast.error('An error occurred while creating the task');
          setError('An error occurred while creating the task.');
          setCreateTaskLoading(false);
        })
        .finally(() => {
          setCreateTaskLoading(false);
          setIsBusy(false);
        });
    }
  };

  const handleFunctionCall = async (tool) => {
    try {
      setSendTaskLoading(true);

      const finalArguments = prepareFunctionArguments(
        tool,
        currentToolResponseForReference,
      );

      const data = await callFunction(
        {
          id: tool.id,
          name: tool.function.name,
          // arguments: tool.function.arguments,
          arguments: finalArguments,
          type: tool.type,
          disapproved: tool.disapproved,
          disapprovalReason: tool.disapprovalReason,
          task_id: currentTaskId,
        },
        userGoogleId,
      );

      extractAndSetToolResponsesForReferencing(
        data,
        setCurrentToolResponseForReference,
      );

      setCurrentToolCallStatus((prevToolStatus) =>
        prevToolStatus.map((prevTool) =>
          prevTool.id !== tool.id
            ? prevTool
            : data.is_error || data.error
              ? { ...prevTool, status: 'failed' }
              : { ...prevTool, status: 'success' },
        ),
      );

      if (data.error) {
        setError(data.error);
        return false;
      } else {
        if (
          tool.function.name === 'download_and_save_attachments_on_gmail' ||
          tool.function.name ===
            'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations' ||
          tool.function.name === 'download_files_google_drive' ||
          tool.function.name ===
            'download_files_from_shared_urls_from_google_drive' ||
          tool.function.name === 'create_docx_document' ||
          tool.function.name === 'download_file_from_onedrive'
        ) {
          // setJustSavedFiles(false);
          setJustSavedFiles(true);
        }
        if (!data.is_error) {
          if (
            tool.function?.name?.startsWith('handoff_to') ||
            tool.function?.name?.startsWith('return_to')
          ) {
            if (data.agent_sequence) setAgentSequence(data.agent_sequence);
          }
        }

        setError(null);
        return data;
      }
    } catch (error) {
      toast.error(`${error}`);
      setError('An error occurred while calling the function.');
      return false;
    } finally {
      setSendTaskLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    setIsBusy(true);
    try {
      await deleteTask(taskId, userGoogleId); // Call your delete API function with token
      setTasks(tasks.filter((task) => task._id !== taskId)); // Update the tasks state to remove the deleted task
      if (currentTaskId === taskId && tasks.length > 1) {
        setCurrentTaskId(tasks[1]._id);
        loadConversations(tasks[1]._id);
      } else if (tasks.length === 1) {
        setCurrentTaskId(null);
        dispatchConversations({ type: 'TASK_SWITCHED' });
        setTools([]);
      }
      setError(null);
    } catch (error) {
      toast.error('Error deleting task');
      setError('An error occurred while deleting the task.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleAutomaticChange = (e) => {
    setIsAutomatic(e.target.checked);
    if (e.target.checked) {
      // setShowTools(true);
    } else {
      setShowTools(true);
    }
  };

  const handleShowToolsChange = (e) => {
    if (!isAutomatic) {
      setShowTools(true);
    } else {
      setShowTools(e.target.checked);
    }
  };

  const renderChatContent = (content) => {
    switch (content.type) {
      case 'text':
        let text = content.text;
        if (text.includes('TASK STATUS:')) {
          // Split the text into two parts
          const parts = text.split('TASK STATUS:');
          text = parts[0].trim();
        }
        return <TextFormat response={text} sources={[]} />;
      case 'tool_use':
        return (
          <div>
            <TextFormat response={content.name} sources={[]} />
            {!isAutomatic && !content.handled && (
              <button onClick={() => handleFunctionCall(content)}>
                Confirm
              </button>
            )}
            {content.handled && <p>Tool Request Handled</p>}
          </div>
        );
      case 'tool_result':
        return (
          <div>
            {typeof content.content === 'object' ? (
              <TextFormat
                response={JSON.stringify(content.content, null, 2)}
                sources={[]}
              />
            ) : (
              <TextFormat response={content.content} sources={[]} />
            )}
            {content.is_error && (
              <TextFormat response={content.content} sources={[]} />
            )}
          </div>
        );
      case 'step_response':
        let text1 = content.text;
        return (
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-gray-100 p-4 text-gray-900 dark:bg-gray-700 dark:text-gray-100">
            {text1}
          </pre>
        );
      case 'session':
        let session_id = content.session_id;
        let bot_id = content.bot_id;
        return (
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-gray-100 p-4 text-gray-900 dark:bg-gray-700 dark:text-gray-100">
            Session ID: {session_id} Bot ID: {bot_id}
          </pre>
        );
      // return <TextFormat response={text1} sources={[]} />;
      default:
        return <p>Unknown content type</p>;
    }
  };

  const [showUploadModal, setShowUploadModal] = useState(false);
  // const scrollToBottom = () => {
  //   if (messageContainerRef.current) {
  //     messageContainerRef.current.scrollTop =
  //       messageContainerRef.current.scrollHeight;
  //   }
  // };
  const scrollToBottom = () => {
    const container = messageContainerRef.current;
    if (!container) return;

    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      75;

    if (isAtBottom) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const [files, setFiles] = useState([]);
  return (
    <DataContext.Provider
      value={{
        trackExecution,
        scrollToBottom,
        tasks,
        setTasks,
        showUploadModal,
        setShowUploadModal,
        currentTaskId,
        setCurrentTaskId,
        conversations,
        dispatchConversations,
        input,
        setInput,
        isAutomatic,
        setIsAutomatic,
        taskInput,
        setTaskInput,
        setNewTaskSubmitted,
        setCreateTaskLoading,
        // currentChat,
        // setCurrentChat,
        creatingTask,
        setCreatingTask,
        error,
        setError,
        tools,
        setTools,
        userGoogleId,
        setUserGoogleId,
        subtasks,
        setSubtasks,
        showTools,
        setShowTools,
        hasTaskResults,
        setHasTaskResults,
        isBusy,
        setIsBusy,
        loadConversations,
        renderChatContent,
        handleShowToolsChange,
        handleAutomaticChange,
        handleSubmitTask,
        handleCreateTask,
        // handleSend,
        steps,
        setSteps,
        messageContainerRef,
        justSavedFiles,
        setJustSavedFiles,
        createTaskLoading,
        sendTaskLoading,
        sendSubTaskLoading,
        files,
        setFiles,
        isCreatingSubTask,
        setIsCreatingSubTask,
        handleDeleteTask,
        userInfo,
        streamingResponse,
        handleStreamSend,
        bots,
        setBots,
        botsHistory,
        setBotsHistory,
        currentBotSessions,
        setCurrentBotSessions,
        setCurrentBotSession,
        currentBotSession,
        setCurrentBotId,
        currentBotId,
        setCurrentBot,
        currentBot,
        clarificationQuestions,
        setClarificationQuestions,
        needBotFetch,
        setNeedBotFetch,
        createAndHandleClarificationQuestions,
        botCreationId,
        setBotCreationId,
        submitClarificationAnswersAndCreateBotSteps,
        setSubmitClarificationQuestions,
        submitClarificationQuestions,
        isBotStepsModalOpen,
        selectedBotId,
        closeBotStepsModal,
        botTaskId,
        setBotTaskId,
        showBotSteps,
        setShowBotSteps,
        justStartedBot,
        setJustStartedBot,
        botStepInputToSubmit,
        setBotStepInputToSubmit,
        useAutomaticStepSwitching,
        setUseAutomaticStepSwitching,
        moveToNextStep,
        setMoveToNextStep,
        theme,
        setTheme,
        botSessionJustCreatedId,
        setBotSessionJustCreatedId,
        currentTaskSessions,
        setCurrentTaskSessions,
        currentBotSessionChats,
        setCurrentBotSessionChats,
        newTriggerName,
        setNewTriggerName,
        fetchFiles,
        allUserDataSourceFiles,
        setAllUserDataSourceFiles,
        currentTaskDataSourceFiles,
        setCurrentTaskDataSourceFiles,
        fetchDataSourceFiles,
        feedbackModalOpen,
        setFeedbackModalOpen,
        toolCallApprovalList,
        setToolCallApprovalList,
        justChangedLocalStorageData,
        setJustChangedLocalStorageData,
        currentToolResults,
        setCurrentToolResults,
        currentTaskImages,
        setCurrentTaskImages,
        workflows,
        setWorkflows,
        currentToolCallStatus,
        setCurrentToolCallStatus,
        currentToolCallStatusMap,
        currentReasoningCalls,
        setCurrentReasoningCalls,
        setCurrentReasoningToolStreamingData,
        currentReasoningToolStreamingData,
        selectedReason,
        setSelectedReason,
        isWebSearch,
        setIsWebSearch,
        isReasoning,
        setIsReasoning,
        isCanvas,
        setIsCanvas,
        isAgentMode,
        setIsAgentMode,
        agentSequence,
        setAgentSequence,
        agentTraversalPaths,
        setAgentTraversalPaths,
        toggleSidebar,
        setToggleSidebar,
        currentToolResponseForReference,
        taskMCPConnections,
        setTaskMCPConnections,
        taskOqlousTools,
        setTaskOqlousTools,
        taskManualToolSelection,
        setTaskManualToolSelection,
        scrollToToolId,
        setScrollToToolId,
        rightSidebarWidth,
        setRightSidebarWidth,
        currentTaskPlan,
        setCurrentTaskPlan,
        currentTaskModel,
        setCurrentTaskModel,
        // openNotifications,
        // setOpenNotifications,
        // activeNotificationId,
        // setActiveNotificationId,
        // collapsedNotificationIds,
        // setCollapsedNotificationIds,
        // addOpenNotification,
        // removeCollapsedNotificationId,
        // createNewTaskForNotification,
        stopStreaming, // Add the new function here
        isStreaming, // Add the new state here
        hasPendingToolCalls,
        statusMessage,
        setStatusMessage,
        detachActiveTaskStreams,
        handleFunctionCall,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
